import { FC, useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Box,
    CircularProgress,
    Alert,
    Grid,
    TextField,
    MenuItem,
    Tabs,
    Tab,
} from '@mui/material';
import { useCategoryStats, useLanguageStats } from '../../hooks/useLibrary';

// ✅ 언어 옵션
const LANGUAGE_OPTIONS = [
    { value: '', label: '전체' },
    { value: 'ko', label: '한국어' },
    { value: 'en', label: '영어' },
    { value: 'ja', label: '일본어' },
    { value: 'zh', label: '중국어' },
];

export const LibraryStats: FC = () => {
    const [tabValue, setTabValue] = useState(0);
    const [headLang, setHeadLang] = useState('');
    const [meaningLang, setMeaningLang] = useState('');

    // ✅ 언어 필터 적용
    const { data: categoryStats, isLoading: categoryLoading, error: categoryError } =
        useCategoryStats(headLang || undefined, meaningLang || undefined);

    // ✅ 언어별 통계 추가
    const { data: languageStats, isLoading: languageLoading, error: languageError } =
        useLanguageStats();

    if (categoryLoading || languageLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (categoryError || languageError) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    통계를 불러오는데 실패했습니다:{' '}
                    {categoryError?.message || languageError?.message}
                </Alert>
            </Box>
        );
    }

    const n = (value: unknown): number => Number(value ?? 0);
    const categoryList = categoryStats?.categories ?? [];
    const languageList = languageStats?.languages ?? [];

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                라이브러리 통계
            </Typography>

            {/* ✅ 탭 추가 */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
                    <Tab label="카테고리별" />
                    <Tab label="언어별" />
                </Tabs>
            </Box>

            {/* 카테고리별 통계 */}
            {tabValue === 0 && categoryStats && (
                <>
                    {/* ✅ 언어 필터 추가 */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                select
                                label="단어 언어"
                                value={headLang}
                                onChange={(e) => setHeadLang(e.target.value)}
                                size="small"
                                fullWidth
                            >
                                {LANGUAGE_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                select
                                label="의미 언어"
                                value={meaningLang}
                                onChange={(e) => setMeaningLang(e.target.value)}
                                size="small"
                                fullWidth
                            >
                                {LANGUAGE_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>

                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" color="textSecondary">
                                        전체 라이브러리
                                    </Typography>
                                    <Typography variant="h3">
                                        {n(categoryStats?.totalLibraries)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" color="textSecondary">
                                        전체 다운로드
                                    </Typography>
                                    <Typography variant="h3">
                                        {n(categoryStats?.totalDownloads).toLocaleString()}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                카테고리별 통계
                            </Typography>

                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>카테고리</TableCell>
                                        <TableCell align="right">전체</TableCell>
                                        <TableCell align="right">노출 중</TableCell>
                                        <TableCell align="right">다운로드</TableCell>
                                        <TableCell align="right">평균 평점</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {categoryList.map((category, idx) => (
                                        <TableRow key={`${category.category ?? 'unknown'}-${idx}`}>
                                            <TableCell>
                                                <strong>{String(category.category ?? '-')}</strong>
                                            </TableCell>
                                            <TableCell align="right">
                                                {category.totalLibraries}
                                            </TableCell>
                                            <TableCell align="right">
                                                {category.visibleLibraries}
                                            </TableCell>
                                            <TableCell align="right">
                                                {n(category.totalDownloads).toLocaleString()}
                                            </TableCell>
                                            <TableCell align="right">
                                                ⭐ {n(category.averageRating).toFixed(1)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}

            {/* ✅ 언어별 통계 */}
            {tabValue === 1 && languageStats && (
                <>
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" color="textSecondary">
                                        전체 라이브러리
                                    </Typography>
                                    <Typography variant="h3">
                                        {n(languageStats?.totalLibraries)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                언어 조합별 통계
                            </Typography>

                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>언어 조합</TableCell>
                                        <TableCell align="right">전체</TableCell>
                                        <TableCell align="right">노출 중</TableCell>
                                        <TableCell align="right">총 단어 수</TableCell>
                                        <TableCell align="right">다운로드</TableCell>
                                        <TableCell align="right">평균 평점</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {languageList.map((lang, idx) => (
                                        <TableRow key={`${lang.headLang ?? 'unknown'}-${lang.meaningLang ?? 'unknown'}-${idx}`}>
                                            <TableCell>
                                                <strong>
                                                    {String(lang.headLang ?? '-').toUpperCase()} →{' '}
                                                    {String(lang.meaningLang ?? '-').toUpperCase()}
                                                </strong>
                                            </TableCell>
                                            <TableCell align="right">
                                                {lang.totalLibraries}
                                            </TableCell>
                                            <TableCell align="right">
                                                {lang.visibleLibraries}
                                            </TableCell>
                                            <TableCell align="right">
                                                {n(lang.totalWords).toLocaleString()}
                                            </TableCell>
                                            <TableCell align="right">
                                                {n(lang.totalDownloads).toLocaleString()}
                                            </TableCell>
                                            <TableCell align="right">
                                                ⭐ {n(lang.averageRating).toFixed(1)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}
        </Box>
    );
};
